import React, { useState } from 'react';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'; // v2 import

const Training = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);

  const trainingPrograms = [
    {
      title: 'ENTERPRISE SECURITY AWARENESS TRAINING',
      image: '/images/security-awareness.jpg',
      alt: 'Security Awareness Training Photo by Frederick Shaw on Unsplash',
      description: 'Comprehensive security education for enterprise teams to build a security-first culture and prevent threats.',
      items: [
        'Fundamentals of IT Security',
        'Software Security Awareness',
        'Fundamentals of Information Security',
        'Fundamentals of the PCI-DSS',
        'Mobile & Social Media Security',
        'End user preventive measures',
        'Microsoft Trainings',
        'Cisco Trainings',
        'Ethical hacking and forensic',
      ],
    },
    {
      title: 'NETWORK FUNDAMENTAL AND INCIDENCE RESPONSE TRAINING',
      image: '/images/banner2.jpg',
      alt: 'Network & Incident Response Training',
      description: 'Master network fundamentals and develop effective incident response strategies for modern IT environments.',
      items: [
        'Fundamentals of Networking',
        'Responding to incidences with reference to OSI Model',
        'Incidence identification, definition and containment',
        'Layer1 and layer2 Network Troubleshooting',
        'Incidence analysis and treatment',
        'Documentation procedure',
        'Issue Reporting and Escalations procedures',
      ],
    },
    {
      title: 'SECURITY TESTING TRAINING',
      image: '/images/aa.jpg',
      alt: 'Security Testing Training',
      description: 'Hands-on training in security testing methodologies, tools, and techniques for robust system protection.',
      items: [
        'Fundamentals of Information System',
        'Hacking Fundamentals of Security Testing',
        'Security Testing for Web Applications',
        'Tools & Techniques for penetration testing',
        'Exploiting Buffer Overflows and SQL Injections',
      ],
    },
    {
      title: 'ISO22301 & ISO27001 Training',
      image: '/images/u.jpg',
      alt: 'ISO Standards Training',
      description: 'Expert-led training on implementing and auditing ISO standards for business continuity and information security.',
      items: [
        'Implementation and auditing of Business Continuity Management system',
        'Implementation and auditing of Information security management system',
        'ISO27001 Training and certification guidance',
      ],
    },
  ];

  const handleSeeMore = (index) => {
    if (expandedCard === index) {
      setExpandedCard(null);
      setExpandedModule(null);
    } else {
      setExpandedCard(index);
      setExpandedModule(null);
    }
  };

  const handleModuleClick = (moduleIndex, e) => {
    e.stopPropagation();
    setExpandedModule(expandedModule === moduleIndex ? null : moduleIndex);
  };

  return (
    <section id="training" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center mb-4 bg-gray-800 rounded-full px-4 py-2 border border-gray-700">
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2 animate-pulse"></div>
            <span className="text-blue-400 font-medium">Professional Training</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#fff8f3] mb-4">
            Our Training Programs
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-6">
            Professional development programs designed to enhance skills and knowledge in modern IT security and compliance
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-400 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {trainingPrograms.map((program, index) => (
            <div
              key={index}
              className={`group bg-gray-800 rounded-2xl shadow-2xl transition-all duration-500 overflow-hidden border ${
                expandedCard === index 
                  ? 'border-blue-500 scale-105 z-20 shadow-2xl shadow-blue-500/20' 
                  : expandedCard !== null 
                    ? 'opacity-30 scale-95 blur-[2px] border-gray-700' 
                    : 'border-gray-700 hover:border-blue-500/50'
              }`}
              style={{
                transform: expandedCard === index ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className="flex flex-col md:flex-row h-full">
                {/* Image Card - Horizontal layout */}
                <div className="relative h-48 md:h-auto md:w-5/12 lg:w-2/5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-600/10 z-10"></div>
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${program.image})` }}
                    alt={program.alt}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent z-20"></div>
                  
                  {/* Badge */}
                  <div className="absolute top-4 left-4 z-30 bg-gray-900/90 backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700">
                    <span className="text-sm font-semibold text-blue-400">Training</span>
                  </div>
                  
                  {/* Overlay Icon */}
                  <div className="absolute bottom-4 right-4 z-30 bg-blue-600 rounded-full p-2 shadow-lg">
                    <svg className="w-6 h-6 text-[#fff8f3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6 md:p-8 md:w-7/12 lg:w-3/5 relative">
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center mr-3">
                        <span className="text-blue-400 font-bold">{index + 1}</span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-[#fff8f3] group-hover:text-blue-400 transition-colors duration-300">
                        {program.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 mb-6">
                      {program.description}
                    </p>
                  </div>
                  
                  {/* See More Button */}
                  <button
                    onClick={() => handleSeeMore(index)}
                    className="inline-flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-[#fff8f3] px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 group/btn w-full border border-gray-600"
                  >
                    <span>{expandedCard === index ? 'Show Less' : 'See More'}</span>
                    {expandedCard === index ? (
                      <ChevronUpIcon className="w-4 h-4 ml-2" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 ml-2" />
                    )}
                  </button>
                  
                  {/* Course Modules - Slides from the side when expanded */}
                  <div 
                    className={`absolute top-0 right-0 h-full w-96 bg-gray-900 transform transition-all duration-500 ease-in-out ${
                      expandedCard === index 
                        ? 'translate-x-0 opacity-100' 
                        : 'translate-x-full opacity-0'
                    } overflow-y-auto border-l border-gray-700 shadow-2xl`}
                    style={{
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-semibold text-[#fff8f3]">Course Modules</h4>
                        <button 
                          onClick={() => handleSeeMore(index)}
                          className="text-gray-400 hover:text-[#fff8f3]"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <ul className="space-y-2">
                        {program.items.map((item, itemIndex) => (
                          <li key={itemIndex}>
                            <button
                              onClick={(e) => handleModuleClick(itemIndex, e)}
                              className={`w-full text-left p-3 rounded-lg transition-all duration-300 ${
                                expandedModule === itemIndex
                                  ? 'bg-blue-600 text-[#fff8f3] scale-105 shadow-lg shadow-blue-600/20'
                                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-[#fff8f3]'
                              }`}
                            >
                              <div className="flex items-center">
                                <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 ${
                                  expandedModule === itemIndex ? 'text-[#fff8f3]' : 'text-blue-500'
                                }`} />
                                <span>{item}</span>
                              </div>
                              
                              {/* Expanded module details */}
                              {expandedModule === itemIndex && (
                                <div className="mt-3 pt-3 border-t border-blue-400/30 text-sm text-blue-100">
                                  <p className="mb-2">Detailed module information:</p>
                                  <ul className="list-disc list-inside space-y-1">
                                    <li>Duration: 2-3 hours</li>
                                    <li>Hands-on exercises included</li>
                                    <li>Certificate of completion</li>
                                  </ul>
                                </div>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                      
                      {/* Enroll Button in Side Panel */}
                      <div className="mt-6 pt-6 border-t border-gray-700">
                        <a
                          href="#contact"
                          className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-[#fff8f3] px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 group/btn w-full"
                        >
                          <span>Enroll Now</span>
                          <svg className="w-4 h-4 ml-2 transform group-hover/btn:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Hover Effect Border */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-hover:opacity-100 transform scale-x-0 group-hover:scale-x-100 transition-all duration-500"></div>
            </div>
          ))}
        </div>

        {/* Free Training Materials Banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 shadow-2xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), 
                              radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
            }}></div>
          </div>
          
          <div className="relative px-8 py-12 md:px-12 lg:px-16 text-center text-[#fff8f3]">
            <div className="inline-flex items-center mb-4 bg-[#fff8f3]/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30">
              <div className="w-2 h-2 rounded-full bg-[#fff8f3] animate-pulse mr-2"></div>
              <span className="text-sm font-medium">Limited Time Offer</span>
            </div>
            
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Get Free Training Materials
            </h3>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              Contact us today to receive free training materials and a personalized consultation session
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="group bg-[#fff8f3] text-blue-700 hover:bg-gray-100 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center shadow-lg hover:shadow-xl"
              >
                <span>Get Free Materials</span>
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="#contact"
                className="group bg-transparent border-2 border-white/30 hover:bg-[#fff8f3]/10 text-[#fff8f3] hover:text-[#fff8f3] px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center backdrop-blur-sm"
              >
                <span>Schedule Consultation</span>
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </a>
            </div>
            
            <p className="mt-8 text-blue-200 text-sm">
              * Free materials include course outlines, e-books, and practice exercises
            </p>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32">
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent rounded-bl-3xl"></div>
          </div>
          <div className="absolute bottom-0 left-0 w-24 h-24">
            <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-tr from-white/10 to-transparent rounded-tr-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Training;